import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Drawer, Input, Typography } from 'antd';
import { Copy, Trash2, Pencil, Plus, Search } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { BuilderTable, type BuilderTableColumn } from '@/components/BuilderTable';
import { BuilderChip } from '@/components/BuilderChip';
import { BuilderIconButton } from '@/components/BuilderIconButton';
import { DrawerHeader } from '@/components/DrawerHeader';
import { useBreakpoint, useDrawerSurface } from '@/hooks';
import { QuestionSetDetailSheet } from './QuestionSetDetailSheet';
import { AdminQuestionSetsService, BaseAPIClient } from '@/services/api';
import type { QuestionSetWithPackages } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import { ROUTES, buildRoute } from '@/routes';
import { containerVariants, itemVariants } from '@/utils';
import styles from './AdminQuestionSets.module.scss';

const { Title, Text } = Typography;

const GRID_TEMPLATE_COLUMNS = 'minmax(220px, 2.5fr) minmax(100px, 0.75fr) minmax(110px, 0.85fr) 140px';

const PER_PAGE = 20;

export const QuestionSetList: React.FC = () => {
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();
  const navigate = useNavigate();

  const [questionSets, setQuestionSets] = useState<QuestionSetWithPackages[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<QuestionSetWithPackages | null>(null);
  const { isMobile } = useBreakpoint();
  const drawerSurface = useDrawerSurface();

  const getService = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return new AdminQuestionSetsService(token);
  }, [getToken]);

  const fetchQuestionSets = useCallback(async () => {
    setLoading(true);
    try {
      const svc = await getService();
      setQuestionSets(await svc.list());
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getService, notification]);

  useEffect(() => {
    void fetchQuestionSets();
  }, [fetchQuestionSets]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return questionSets.filter((qs) => {
      if (!term) return true;
      return qs.name.toLowerCase().includes(term);
    });
  }, [questionSets, search]);

  const openEditor = (id: string) => navigate(buildRoute(ROUTES.ADMIN_QUESTION_SET_EDIT, { id }));

  const handleDuplicate = async (qs: QuestionSetWithPackages) => {
    try {
      const svc = await getService();
      await svc.create({
        name: `${qs.name} (Copy)`,
        description: qs.description ?? undefined,
        items: qs.items.map((item) => ({ questionId: item.questionId, order: item.order })),
      });
      notification.success({ message: 'Question set duplicated' });
      await fetchQuestionSets();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const handleDelete = async (qs: QuestionSetWithPackages) => {
    try {
      const svc = await getService();
      await svc.remove(qs.id);
      notification.success({ message: 'Question set deleted' });
      await fetchQuestionSets();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const goToPackage = (id: string) => navigate(buildRoute(ROUTES.ADMIN_PACKAGE_EDIT, { id }));

  const confirmDelete = (qs: QuestionSetWithPackages) => {
    const inUse = qs.packages.length > 0;
    const instance = modal.confirm({
      title: 'Delete this question set?',
      icon: null,
      maskClosable: true,
      centered: true,
      content: inUse ? (
        <div className={styles.deleteHint}>
          <div>Unassign it from these packages before it can be deleted:</div>
          <div className={styles.packageRefs}>
            {qs.packages.map((pkg) => (
              <BuilderChip
                key={pkg.id}
                tint="purple"
                onClick={() => {
                  instance.destroy();
                  goToPackage(pkg.id);
                }}
              >
                {pkg.name}
              </BuilderChip>
            ))}
          </div>
        </div>
      ) : (
        "This cannot be undone."
      ),
      okText: 'Delete',
      okButtonProps: { danger: true, disabled: inUse },
      onOk: () => handleDelete(qs),
    });
  };

  const columns: BuilderTableColumn<QuestionSetWithPackages>[] = [
    {
      key: 'name',
      title: 'Question Set',
      sortFn: (a, b) => a.name.localeCompare(b.name),
      render: (qs) => (
        <span className={styles.cellName}>
          {qs.name}
          {qs.htsQuestionnaireId !== null && <span className={styles.htsBadge}>HTS</span>}
        </span>
      ),
    },
    {
      key: 'questions',
      title: 'Questions',
      sortFn: (a, b) => a.items.length - b.items.length,
      render: (qs) => (
        <span className={styles.cellMuted}>
          {qs.items.length} question{qs.items.length !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'updated',
      title: 'Updated',
      sortFn: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (qs) => <span className={styles.cellDate}>{formatDate(qs.updatedAt)}</span>,
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (qs) => (
        <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
          <BuilderIconButton icon={<Pencil style={{ width: '1em', height: '1em' }} />} title="Edit" onClick={() => openEditor(qs.id)} />
          <BuilderIconButton icon={<Copy style={{ width: '1em', height: '1em' }} />} title="Duplicate" onClick={() => handleDuplicate(qs)} />
          <BuilderIconButton
            icon={<Trash2 style={{ width: '1em', height: '1em' }} />}
            title="Delete"
            danger
            onClick={() => confirmDelete(qs)}
          />
        </div>
      ),
    },
  ];

  return (
    <motion.div className={styles.page} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div className={styles.pageHead} variants={itemVariants}>
        <div>
          <Title level={2} className={styles.pageTitle}>
            Question Sets
          </Title>
          <Text className={styles.pageSub}>
            A named set of questions pulled from the Question Bank. Group these into assessment
            packages.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<Plus style={{ width: '1em', height: '1em' }} />}
          className={styles.btnPrimary}
          onClick={() => navigate(ROUTES.ADMIN_QUESTION_SET_NEW)}
        >
          Add Question Set
        </Button>
      </motion.div>

      <motion.div className={styles.toolbar} variants={itemVariants}>
        <div className={styles.search}>
          <Search className={styles.searchIcon} style={{ width: '1em', height: '1em' }} />
          <Input
            placeholder="Search question sets…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
      <BuilderTable<QuestionSetWithPackages>
        columns={columns}
        gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
        data={filtered}
        rowKey={(qs) => qs.id}
        pageSize={PER_PAGE}
        loading={loading}
        onRowClick={(qs) => (isMobile ? setViewing(qs) : openEditor(qs.id))}
        mobileActionsLabel={(qs) => `Actions for ${qs.name}`}
        onMobileDetails={(qs) => setViewing(qs)}
        emptyTitle={questionSets.length ? 'No question sets match' : 'No question sets yet'}
        emptySub={
          questionSets.length
            ? 'Adjust the search.'
            : 'Use "Add Question Set" to select questions from the bank and group them.'
        }
      />
      </motion.div>

      <Drawer
        title={<DrawerHeader title="Question Set" onClose={() => setViewing(null)} />}
        closable={false}
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        {...drawerSurface}
        destroyOnHidden
        className={styles.drawer}
      >
        {viewing && (
          <QuestionSetDetailSheet
            questionSet={viewing}
            onEdit={() => openEditor(viewing.id)}
            onDuplicate={() => {
              setViewing(null);
              void handleDuplicate(viewing);
            }}
            onDelete={() => {
              setViewing(null);
              confirmDelete(viewing);
            }}
            onNavigateToPackage={(id) => {
              setViewing(null);
              goToPackage(id);
            }}
          />
        )}
      </Drawer>
    </motion.div>
  );
};

export default QuestionSetList;
